<i18n>
{
  "fr": {
    "business-list-name-label": "Entreprise",
    "business-list-jurisdiction-label": "Juridiction",
    "business-list-status-label": "Statut",
    "business-list-state-label": "État",
    "business-list-state-enabled": "Actif",
    "business-list-state-disabled": "Inactif",
    "business-list-subscription-date-label": "Date d'abonnement",
    "business-list-applicant-label": "Requérant",
    "business-list-position-label": "Position",
    "business-list-phone-label": "Téléphone",
    "business-list-email-label": "Courriel",
    "business-status-public-text": "Publique",
    "business-status-private-text": "Privée",

    "delete-business-title": "Supprimer l'entreprise",
    "delete-business-message": "Vous vous apprêtez à supprimer l'entreprise <strong>«\u00A0{name}\u00A0»</strong> qui contient <strong>{employeeCount, plural, one {un utilisateur} other {# utilisateurs}}</strong> et <strong>{projectCount, plural, =0 {aucun projet} one {un projet} other {# projets}}</strong>.<br><br> Cette action est irréversible. Souhaitez-vous continuer?",
    "delete-business-cancel": "Annuler",
    "delete-business-confirm": "Supprimer",

    "update-business-title": "Modifier l'entreprise",
    "update-business-name": "Nom de l'entreprise",
    "update-business-subscription-date": "Date d'abonnement",
    "update-business-state": "État",
    "update-business-cancel": "Annuler",
    "update-business-confirm": "Mettre à jour"
  },
  "en": {
    "business-list-name-label": "Company",
    "business-list-jurisdiction-label": "Jurisdiction",
    "business-list-status-label": "Status",
    "business-list-state-label": "State",
    "business-list-state-enabled": "Active",
    "business-list-state-disabled": "Inactive",
    "business-list-subscription-date-label": "Subscription date",
    "business-list-applicant-label": "Applicant",
    "business-list-position-label": "Position",
    "business-list-phone-label": "Phone",
    "business-list-email-label": "Email",
    "business-status-public-text": "Public",
    "business-status-private-text": "Private",

    "delete-business-title": "Delete company",
    "delete-business-message": "You are about to delete the company \"{name}\" which has <strong>{employeeCount, plural, one {one user} other {# users}}</strong> and <strong>{projectCount, plural, =0 {no projects} one {one project} other {# projects}}</strong>.<br><br> This action cannot be reverted. Do you want to continue?",
    "delete-business-cancel": "Cancel",
    "delete-business-confirm": "Delete",

    "update-business-title": "Edit company",
    "update-business-name": "Company name",
    "update-business-subscription-date": "Subscription date",
    "update-business-state": "State",
    "update-business-cancel": "Cancel",
    "update-business-confirm": "Update"
  }
}
</i18n>

<template>
  <div class="business-list-table">
    <s-loader :active="!business && $apollo.queries.businessByCategory.loading" />
    <b-table
      v-if="business"
      :data="business"
      :loading="$apollo.queries.businessByCategory.loading"
      class="has-overflow-auto"
      default-sort="name"
      :row-class="(row, _) => row.disabled && 'has-text-grey-light'"
    >
      <b-table-column v-slot="props" field="name" :label="$t('business-list-name-label')" sortable>{{
        props.row.name
      }}</b-table-column>
      <b-table-column v-slot="props" field="jurisdiction" :label="$t('business-list-jurisdiction-label')" sortable>
        {{ props.row.jurisdiction }}
      </b-table-column>
      <b-table-column v-slot="props" field="status" :label="$t('business-list-status-label')" sortable>
        <span class="tag">{{ translateBusinessState(props.row.status) }}</span>
      </b-table-column>
      <b-table-column v-slot="props" field="disabled" :label="$t('business-list-state-label')" sortable>
        <span v-if="props.row.disabled" class="tag is-danger is-light">{{ $t('business-list-state-disabled') }}</span>
        <span v-else class="tag is-success is-light">{{ $t('business-list-state-enabled') }}</span>
      </b-table-column>
      <b-table-column v-slot="props" field="subscriptionDate" :label="$t('business-list-subscription-date-label')" sortable>
        {{ props.row.subscriptionText }}
      </b-table-column>

      <b-table-column v-slot="props" field="applicant" :label="$t('business-list-applicant-label')" sortable>
        {{ props.row.applicant.name }}
        <br />
        <span class="is-sr-only">{{ $t('business-list-position-label') }}</span> {{ props.row.applicant.position }}
      </b-table-column>
      <b-table-column field="applicantTelephone" :label="$t('business-list-phone-label')">
        <template v-slot:header="{ column }">
          <span class="is-invisible">{{ column.label }}</span>
        </template>
        <template v-slot:default="props">
          {{ props.row.applicant.telephone }}
          <br />
          <a class="has-text-underline" target="_blank" :href="'mailto:' + props.row.applicant.email">
            {{ $t('business-list-email-label') }}
          </a>
        </template>
      </b-table-column>
      <b-table-column v-slot="{ row }">
        <b-field position="is-right">
          <p class="control">
            <b-button @click="promptUpdateBusiness(row)" outlined size="is-small">
              <span class="icon is-small has-text-link"><i class="fas fa-pen" /></span>
            </b-button>
          </p>

          <p class="control">
            <b-button @click="deleteBusiness(row)" outlined size="is-small">
              <span class="icon is-small has-text-link"><i class="far fa-trash-alt" /></span>
            </b-button>
          </p>
        </b-field>
      </b-table-column>
    </b-table>

    <b-modal v-model="showUpdateBusinessModal" has-modal-card class="has-overflow">
      <template #default="{ close }">
        <div class="modal-card">
          <header class="modal-card-head">
            <h2 class="modal-card-title">{{ $t('update-business-title') }}</h2>
            <button type="button" class="delete" @click="close"></button>
          </header>
          <section class="modal-card-body" v-if="!!updateBusinessModel">
            <s-field :label="$t('update-business-name')">
              <b-input v-model="updateBusinessModel.name" />
            </s-field>
            <s-field :label="$t('update-business-subscription-date')">
              <s-locale-date
                icon="calendar-day"
                :placeholder="$t('update-business-subscription-date')"
                v-model="updateBusinessModel.subscriptionDate"
              ></s-locale-date>
            </s-field>
            <s-field :label="$t('update-business-state')">
              <!-- Switch "activée" quand l'état n'est pas disabled : inversion des valeurs true/false -->
              <b-switch v-model="updateBusinessModel.disabled" :true-value="false" :false-value="true" />
              {{ updateBusinessModel.disabled ? $t('business-list-state-disabled') : $t('business-list-state-enabled') }}
            </s-field>
          </section>
          <footer class="modal-card-foot is-justify-content-end">
            <b-button @click="closeUpdateBusiness">
              {{ $t('update-business-cancel') }}
            </b-button>
            <b-button @click="updateBusiness" :loading="updateBusinessLoading" type="is-primary">
              {{ $t('update-business-confirm') }}
            </b-button>
          </footer>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import SField from '@/components/form/field'
import SLocaleDate from '@/components/form/locale-date'
import { BUSINESS_STATUS_PUBLIC, BUSINESS_STATUS_PRIVATE } from '@/consts/enums'
import JurisdictionService from '@/services/jurisdiction'

const BUSINESS_LIST_LIMIT = 100

export default {
  components: { SField, SLocaleDate },
  data() {
    return {
      page: 1,
      showUpdateBusinessModal: false,
      updateBusinessModel: null,
      updateBusinessLoading: false
    }
  },
  props: {
    category: {
      type: String,
      required: true
    }
  },
  computed: {
    business: function () {
      if (this.businessByCategory === undefined) {
        return null
      }

      return this.businessByCategory.items.map((x) => {
        if (!x.applicant) x.applicant = {}
        if (!x.applicant.profile) x.applicant.profile = { firstName: '', lastName: '' }
        if (!x.applicantUserBusiness) x.applicantUserBusiness = {}

        return {
          id: x.id,
          name: x.name,
          status: x.status,
          jurisdiction: JurisdictionService.getJurisdictionDisplay(x.jurisdictionCode, x.jurisdictionOther),
          subscriptionDate: x.subscriptionDate,
          subscriptionText: this.$format.date(x.subscriptionDate),
          disabled: x.disabled,
          applicant: {
            name: x.applicant.profile.firstName + ' ' + x.applicant.profile.lastName,
            position: x.applicantUserBusiness.position,
            telephone: x.applicant.profile.telephone,
            email: x.applicant.email
          }
        }
      })
    }
  },
  methods: {
    translateBusinessState(state) {
      switch (state) {
        case BUSINESS_STATUS_PUBLIC:
          return this.$t('business-status-public-text')
        case BUSINESS_STATUS_PRIVATE:
          return this.$t('business-status-private-text')
        default:
          return ''
      }
    },
    async deleteBusiness(business) {
      var stats = await this.$apollo.query({
        query: this.$options.query.BusinessStats,
        variables: { businessId: business.id },
        fetchPolicy: 'no-cache'
      })

      this.$buefy.dialog.confirm({
        title: this.$t('delete-business-title'),
        message: this.$t('delete-business-message', {
          name: business.name,
          employeeCount: stats.data.business.employees.length,
          projectCount: stats.data.business.projects.length
        }),
        confirmText: this.$t('delete-business-confirm'),
        cancelText: this.$t('delete-business-cancel'),
        type: 'is-danger',
        onConfirm: async () => {
          await this.$apollo.mutate({
            mutation: this.$options.mutation.DeleteBusiness,
            variables: { businessId: business.id }
          })

          this.$success()

          await this.$apollo.queries.businessByCategory.refetch()
        }
      })
    },
    promptUpdateBusiness(row) {
      this.updateBusinessModel = {
        id: row.id,
        name: row.name,
        subscriptionDate: row.subscriptionDate,
        disabled: row.disabled
      }
      this.showUpdateBusinessModal = true
    },
    closeUpdateBusiness() {
      this.showUpdateBusinessModal = false
      this.updateBusinessModel = null
    },
    async updateBusiness() {
      try {
        this.updateBusinessLoading = true

        await this.$apollo.mutate({
          mutation: this.$options.mutation.UpdateBusiness,
          variables: {
            businessId: this.updateBusinessModel.id,
            name: this.updateBusinessModel.name,
            subscriptionDate: this.updateBusinessModel.subscriptionDate,
            disabled: this.updateBusinessModel.disabled
          }
        })

        this.closeUpdateBusiness()
        this.$success()
      } finally {
        this.updateBusinessLoading = false
      }
    }
  },
  apollo: {
    businessByCategory: {
      query() {
        return this.$options.query.BusinessListTable_BusinessesByCategory
      },
      variables() {
        return {
          category: this.category,
          page: this.page,
          limit: BUSINESS_LIST_LIMIT
        }
      }
    }
  }
}
</script>
<graphql>
query BusinessListTable_BusinessesByCategory($category: BusinessCategory!, $page: Int!, $limit: Int!) {
  businessByCategory(businessCategory: $category, page: $page, limit: $limit) {
    items {
      id
      status
      name
      jurisdictionCode
      jurisdictionOther
      subscriptionDate
      disabled
      applicant {
        id
        email
        profile {
          id
          firstName
          lastName
          telephone
        }
      }
      applicantUserBusiness {
        id
        position
      }
    }
    pageNumber
    pageSize
    totalCount
    totalPages
  }
}

query BusinessStats($businessId: ID!) {
  business(id: $businessId) {
    id
    employees {
      id
    }
    projects {
      id
    }
  }
}

mutation DeleteBusiness($businessId: ID!) {
  deleteBusiness(input: { businessId: $businessId }) {
    success
  }
}

mutation UpdateBusiness($businessId: ID!, $name: String!, $subscriptionDate: DateTime!, $disabled: Boolean!) {
  updateBusiness(
    input: {
      businessId: $businessId
      name: { value: $name }
      subscriptionDate: { value: $subscriptionDate }
      disabled: { value: $disabled }
    }
  ) {
    business {
      id
      name
      subscriptionDate
      disabled
    }
  }
}
</graphql>

<style lang="scss">
@include desktop {
  .business-list-table {
    tr {
      .field.has-addons {
        opacity: 0;
      }

      &:hover .field.has-addons {
        opacity: 1;
      }
    }
  }
}
/* 
  Fix for datepicker inside the modal
  https://github.com/buefy/buefy/issues/292 
*/
.modal.has-overflow {
  position: fixed !important;
  overflow: auto !important;
  .modal-background {
    position: fixed !important;
  }
  .modal-content {
    overflow: visible !important;
  }
  .modal-card {
    overflow: visible !important;
  }
  .modal-card-body {
    overflow: visible !important;
  }
}
</style>